import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ImageUpload } from "@/components/ImageUpload";
import { StatusPill } from "@/pages/Consign";
import { toast } from "sonner";

const CATS = ["art", "cars", "jewellery", "fashion", "watches"];
const emptyP = { title: "", category: "art", maker: "", price: "", year: "", provenance: "", description: "", image: "", featured: false };
const TABS = ["products", "consignments", "offers"];

export default function Curator() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [consignments, setConsignments] = useState([]);
  const [offers, setOffers] = useState([]);
  const [editing, setEditing] = useState(null); // product being edited or new

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/"); return; }
    if (user.role !== "admin") { navigate("/"); return; }
    refresh();
    // eslint-disable-next-line
  }, [user, loading]);

  const refresh = async () => {
    const [p, c, o] = await Promise.all([
      api.get("/admin/products"), api.get("/admin/consignments"), api.get("/admin/offers"),
    ]);
    setProducts(p.data); setConsignments(c.data); setOffers(o.data);
  };

  const saveProduct = async (e) => {
    e.preventDefault();
    const payload = { ...editing, price: parseFloat(editing.price) };
    try {
      if (editing.id) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing.id ? "Piece updated" : "Piece listed — waitlist notified");
      setEditing(null);
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const delProduct = async (id) => {
    await api.delete(`/admin/products/${id}`);
    toast.success("Piece removed");
    refresh();
  };

  const act = async (url, ok) => {
    try { await api.post(url); toast.success(ok); refresh(); }
    catch (err) { toast.error(err.response?.data?.detail || "Action failed"); }
  };

  if (loading || !user || user.role !== "admin") return <div className="min-h-screen bg-[#050505]" />;

  return (
    <div className="min-h-screen bg-[#050505] px-6 pb-32 pt-32 md:px-12 md:pt-40" data-testid="curator-page">
      <div className="mx-auto max-w-[1600px]">
        <p className="text-[11px] uppercase tracking-[0.4em] text-gold">Curator</p>
        <h1 className="font-serif-lux mt-4 text-5xl font-light leading-none text-[#f5f5f0] md:text-7xl">The register</h1>

        <div className="mt-12 flex gap-8 border-b border-white/10">
          {TABS.map((t) => (
            <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)}
              className={`-mb-px border-b-2 pb-4 text-[11px] uppercase tracking-[0.25em] transition-colors ${
                tab === t ? "border-gold text-[#f5f5f0]" : "border-transparent text-[#52525b] hover:text-[#a1a1aa]"}`}>
              {t}{t === "consignments" ? ` (${consignments.filter((c) => c.status === "pending").length})` : t === "offers" ? ` (${offers.filter((o) => o.status === "pending").length})` : ` (${products.length})`}
            </button>
          ))}
        </div>

        {/* PRODUCTS */}
        {tab === "products" && (
          <div className="mt-10">
            <button data-testid="add-product-btn" onClick={() => setEditing(emptyP)}
              className="mb-8 border border-[#f5f5f0] px-6 py-3 text-[11px] uppercase tracking-[0.3em] text-[#f5f5f0] hover:bg-[#f5f5f0] hover:text-black">
              + Add a piece
            </button>
            <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2 lg:grid-cols-4">
              {products.map((p) => (
                <div key={p.id} data-testid="admin-product-row" className="group">
                  <div className="relative aspect-[3/4] overflow-hidden bg-[#0a0a0a]">
                    <img src={p.image} alt="" className="h-full w-full object-cover" />
                    {p.sold && <span className="absolute left-3 top-3 bg-black/60 px-2 py-1 text-[9px] uppercase tracking-[0.25em] text-red-400">Sold</span>}
                  </div>
                  <p className="mt-3 font-serif-lux text-xl font-light text-[#f5f5f0]">{p.title}</p>
                  <p className="text-sm font-light text-[#a1a1aa]">{money(p.price, p.currency)}</p>
                  <div className="mt-2 flex gap-4">
                    <button data-testid="edit-product-btn" onClick={() => setEditing({ ...emptyP, ...p })} className="text-[10px] uppercase tracking-[0.2em] text-gold">Edit</button>
                    <button data-testid="delete-product-btn" onClick={() => delProduct(p.id)} className="text-[10px] uppercase tracking-[0.2em] text-[#52525b] hover:text-red-400">Remove</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CONSIGNMENTS */}
        {tab === "consignments" && (
          <div className="mt-10 divide-y divide-white/10 border-y border-white/10">
            {consignments.length === 0 && <p className="py-8 text-sm text-[#52525b]">No submissions.</p>}
            {consignments.map((c) => (
              <div key={c.id} data-testid="admin-consignment-row" className="flex flex-col gap-4 py-6 md:flex-row md:items-center md:justify-between">
                <div className="flex items-center gap-4">
                  <img src={c.image} alt="" className="h-20 w-20 object-cover" />
                  <div>
                    <p className="font-serif-lux text-2xl font-light text-[#f5f5f0]">{c.title}</p>
                    <p className="text-sm font-light text-[#a1a1aa]">{c.maker} · {money(c.price)} · {c.category}</p>
                    <p className="mt-1 text-[10px] uppercase tracking-[0.25em] text-[#52525b]">by {c.user_name} ({c.user_email})</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <StatusPill status={c.status} />
                  {c.status === "pending" && (
                    <>
                      <button data-testid="approve-consign-btn" onClick={() => act(`/admin/consignments/${c.id}/approve`, "Approved & listed")}
                        className="border border-emerald-500/50 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-emerald-400 hover:bg-emerald-500 hover:text-black">Approve</button>
                      <button data-testid="reject-consign-btn" onClick={() => act(`/admin/consignments/${c.id}/reject`, "Rejected")}
                        className="border border-red-500/50 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-red-400 hover:bg-red-500 hover:text-black">Reject</button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* OFFERS */}
        {tab === "offers" && (
          <div className="mt-10 divide-y divide-white/10 border-y border-white/10">
            {offers.length === 0 && <p className="py-8 text-sm text-[#52525b]">No offers.</p>}
            {offers.map((o) => (
              <div key={o.id} data-testid="admin-offer-row" className="flex flex-col gap-4 py-6 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="font-serif-lux text-2xl font-light text-[#f5f5f0]">{o.product_title}</p>
                  <p className="text-sm font-light text-[#a1a1aa]">
                    Offer <span className="text-gold">{money(o.amount)}</span> · list {money(o.list_price)}
                  </p>
                  <p className="mt-1 text-[10px] uppercase tracking-[0.25em] text-[#52525b]">{o.user_name} ({o.user_email})</p>
                  {o.message && <p className="mt-2 max-w-lg text-sm font-light italic text-[#a1a1aa]">"{o.message}"</p>}
                </div>
                <div className="flex items-center gap-3">
                  <StatusPill status={o.status} />
                  {o.status === "pending" && (
                    <>
                      <button data-testid="accept-offer-btn" onClick={() => act(`/admin/offers/${o.id}/accept`, "Offer accepted")}
                        className="border border-emerald-500/50 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-emerald-400 hover:bg-emerald-500 hover:text-black">Accept</button>
                      <button data-testid="decline-offer-btn" onClick={() => act(`/admin/offers/${o.id}/decline`, "Offer declined")}
                        className="border border-red-500/50 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-red-400 hover:bg-red-500 hover:text-black">Decline</button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* PRODUCT EDITOR */}
      {editing && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/80 p-6 backdrop-blur-sm" data-testid="product-editor">
          <motion.form initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} onSubmit={saveProduct}
            className="my-10 w-full max-w-2xl border border-white/15 bg-[#0a0a0a] p-10">
            <div className="flex items-center justify-between">
              <h2 className="font-serif-lux text-3xl font-light text-[#f5f5f0]">{editing.id ? "Edit piece" : "New piece"}</h2>
              <button type="button" onClick={() => setEditing(null)} className="text-[11px] uppercase tracking-[0.25em] text-[#52525b] hover:text-[#f5f5f0]">Close</button>
            </div>
            <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="md:col-span-2"><ImageUpload value={editing.image} onChange={(v) => setEditing({ ...editing, image: v })} /></div>
              <EF label="Title" testid="ed-title" value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} />
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-[#52525b]">Category</label>
                <select data-testid="ed-category" value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                  className="mt-2 w-full border-b border-white/20 bg-transparent py-3 text-sm capitalize outline-none focus:border-gold">
                  {CATS.map((c) => <option key={c} value={c} className="bg-[#0a0a0a] capitalize">{c}</option>)}
                </select>
              </div>
              <EF label="Maker" testid="ed-maker" value={editing.maker} onChange={(v) => setEditing({ ...editing, maker: v })} />
              <EF label="Price (USD)" testid="ed-price" type="number" value={editing.price} onChange={(v) => setEditing({ ...editing, price: v })} />
              <EF label="Year" testid="ed-year" value={editing.year} onChange={(v) => setEditing({ ...editing, year: v })} />
              <EF label="Provenance" testid="ed-provenance" value={editing.provenance} onChange={(v) => setEditing({ ...editing, provenance: v })} />
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase tracking-[0.25em] text-[#52525b]">Description</label>
                <textarea data-testid="ed-description" rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  className="mt-2 w-full resize-none border border-white/20 bg-transparent p-3 text-sm outline-none focus:border-gold" />
              </div>
              <label className="flex items-center gap-3 text-sm font-light text-[#a1a1aa] md:col-span-2">
                <input data-testid="ed-featured" type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} />
                Feature on the landing page
              </label>
            </div>
            <button data-testid="save-product-btn" type="submit"
              className="hover-invert mt-8 w-full border border-[#f5f5f0] bg-[#f5f5f0] px-6 py-4 text-[11px] uppercase tracking-[0.3em] text-black hover:bg-transparent hover:text-[#f5f5f0]">
              {editing.id ? "Save changes" : "List piece"}
            </button>
          </motion.form>
        </div>
      )}
    </div>
  );
}

const EF = ({ label, testid, type = "text", value, onChange }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.25em] text-[#52525b]">{label}</label>
    <input data-testid={testid} type={type} required value={value} onChange={(e) => onChange(e.target.value)}
      className="mt-2 w-full border-b border-white/20 bg-transparent py-3 text-sm outline-none focus:border-gold" />
  </div>
);
